import React, { useEffect, useRef, useState } from 'react';

/**
 * QWebToggle - A toggle switch component compatible with QWebEngine.
 * 
 * This component avoids using CSS pseudo-elements (:before, :after) and CSS transitions
 * that might not render properly in Qt QWebEngine with hardware acceleration disabled.
 * Instead, it uses real DOM elements and JavaScript-based animations.
 * 
 * @param {boolean} checked - Whether the toggle is checked/enabled
 * @param {function} onChange - Callback when toggle state changes
 * @param {string} labelOn - Text to display when toggle is on (default: "Enabled")
 * @param {string} labelOff - Text to display when toggle is off (default: "Disabled")
 * @param {boolean} showLabel - Whether to show the label (default: true)
 * @param {string} className - Additional class name for the container
 * @param {object} props - Any additional props to pass to the input element
 */
const QWebToggle = ({ 
  checked = false, 
  onChange, 
  labelOn = "Enabled", 
  labelOff = "Disabled", 
  showLabel = true,
  className = "",
  ...props 
}) => {
  const [isChecked, setIsChecked] = useState(checked);
  const knobRef = useRef(null);
  const trackRef = useRef(null);
  const isAnimating = useRef(false);
  const animationFrameRef = useRef(null);

  // Sync with parent state when checked prop changes
  useEffect(() => {
    setIsChecked(checked);
    updateToggleState(checked, false); // Update visually without animation
  }, [checked]);

  // Cleanup any ongoing animations when unmounting
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  /**
   * Animates the toggle knob and background colors
   * @param {boolean} newState - The target state (true=checked, false=unchecked)
   * @param {boolean} shouldAnimate - Whether to animate the transition
   */
  const updateToggleState = (newState, shouldAnimate = true) => {
    if (!knobRef.current || !trackRef.current) return;

    const knob = knobRef.current;
    const track = trackRef.current;
    
    // Get the data-theme attribute to determine which theme is active
    const theme = document.documentElement.getAttribute('data-theme') || 'dark';
    const isDarkTheme = theme === 'dark';
    
    // Define target styles based on theme and state
    const targetStyles = {
      knob: {
        transform: newState ? 'translateX(21px)' : 'translateX(0)',
        backgroundColor: isDarkTheme 
          ? '#f0f0f0' 
          : 'white',
      },
      track: {
        backgroundColor: newState 
          ? 'rgba(24, 144, 255, 0.8)' 
          : isDarkTheme 
            ? 'rgba(53, 58, 65, 0.95)' 
            : 'rgba(210, 215, 220, 0.95)',
        borderColor: newState 
          ? 'rgba(24, 144, 255, 0.9)' 
          : isDarkTheme 
            ? 'rgba(30, 34, 39, 0.9)' 
            : 'rgba(200, 205, 210, 0.9)',
      }
    };

    // If not animating, just set the styles immediately
    if (!shouldAnimate) {
      knob.style.transform = targetStyles.knob.transform;
      knob.style.backgroundColor = targetStyles.knob.backgroundColor;
      track.style.backgroundColor = targetStyles.track.backgroundColor;
      track.style.borderColor = targetStyles.track.borderColor;
      return;
    }

    // Animation parameters
    const duration = 300; // 300ms animation (same as the original .3s transition)
    const startTime = performance.now();
    
    // Get current styles for animation start
    const startStyles = {
      knob: {
        transform: knob.style.transform || 'translateX(0)',
        backgroundColor: knob.style.backgroundColor || (isDarkTheme ? '#f0f0f0' : 'white'),
      },
      track: {
        backgroundColor: track.style.backgroundColor || 
          (isDarkTheme ? 'rgba(53, 58, 65, 0.95)' : 'rgba(210, 215, 220, 0.95)'),
        borderColor: track.style.borderColor || 
          (isDarkTheme ? 'rgba(30, 34, 39, 0.9)' : 'rgba(200, 205, 210, 0.9)'),
      }
    };

    // Parse the translateX value from the transform string
    const parseTranslateX = (transformStr) => {
      const match = transformStr.match(/translateX\((\d+)px\)/);
      return match ? parseInt(match[1], 10) : 0;
    };

    const startX = parseTranslateX(startStyles.knob.transform);
    const targetX = parseTranslateX(targetStyles.knob.transform);

    // Color parsing helper function to convert various color formats to RGBA
    const parseColor = (colorStr) => {
      // Create a temporary element to compute the color
      const temp = document.createElement('div');
      temp.style.color = colorStr;
      document.body.appendChild(temp);
      const computedColor = getComputedStyle(temp).color;
      document.body.removeChild(temp);
      
      // Extract rgba values
      const match = computedColor.match(/rgba?\((\d+), (\d+), (\d+)(?:, ([\d.]+))?\)/);
      if (!match) return { r: 0, g: 0, b: 0, a: 1 }; // Default fallback
      
      return {
        r: parseInt(match[1], 10),
        g: parseInt(match[2], 10),
        b: parseInt(match[3], 10),
        a: match[4] ? parseFloat(match[4]) : 1
      };
    };

    // Parse colors for animation
    const knobStartColor = parseColor(startStyles.knob.backgroundColor);
    const knobTargetColor = parseColor(targetStyles.knob.backgroundColor);
    
    const trackStartColor = parseColor(startStyles.track.backgroundColor);
    const trackTargetColor = parseColor(targetStyles.track.backgroundColor);
    
    const borderStartColor = parseColor(startStyles.track.borderColor);
    const borderTargetColor = parseColor(targetStyles.track.borderColor);

    // If already animating, cancel the previous animation
    if (isAnimating.current && animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    isAnimating.current = true;
    
    // Animation frame function
    const animate = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1); // Clamp to 1
      
      // Ease in-out function for smoother animation
      const easeInOut = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      const easedProgress = easeInOut(progress);
      
      // Interpolate values
      const interpolate = (start, end, progress) => start + (end - start) * progress;
      
      // Update knob position
      const currentX = interpolate(startX, targetX, easedProgress);
      knob.style.transform = `translateX(${currentX}px)`;
      
      // Update colors
      const interpolateRGBA = (start, end, progress) => {
        return {
          r: Math.round(interpolate(start.r, end.r, progress)),
          g: Math.round(interpolate(start.g, end.g, progress)),
          b: Math.round(interpolate(start.b, end.b, progress)),
          a: interpolate(start.a, end.a, progress)
        };
      };
      
      const currentKnobColor = interpolateRGBA(knobStartColor, knobTargetColor, easedProgress);
      knob.style.backgroundColor = `rgba(${currentKnobColor.r}, ${currentKnobColor.g}, ${currentKnobColor.b}, ${currentKnobColor.a})`;
      
      const currentTrackColor = interpolateRGBA(trackStartColor, trackTargetColor, easedProgress);
      track.style.backgroundColor = `rgba(${currentTrackColor.r}, ${currentTrackColor.g}, ${currentTrackColor.b}, ${currentTrackColor.a})`;
      
      const currentBorderColor = interpolateRGBA(borderStartColor, borderTargetColor, easedProgress);
      track.style.borderColor = `rgba(${currentBorderColor.r}, ${currentBorderColor.g}, ${currentBorderColor.b}, ${currentBorderColor.a})`;
      
      // Continue animation if not complete
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        isAnimating.current = false;
        animationFrameRef.current = null;
      }
    };
    
    // Start animation
    animationFrameRef.current = requestAnimationFrame(animate);
  };

  const handleChange = (e) => {
    const newCheckedState = e.target.checked;
    setIsChecked(newCheckedState);
    updateToggleState(newCheckedState);
    
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className={`qweb-toggle-container ${className}`}>
      <label className="qweb-toggle-switch">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={handleChange}
          className="qweb-toggle-checkbox"
          aria-label={isChecked ? labelOn : labelOff}
          {...props}
        />
        <div className="qweb-toggle-track" ref={trackRef}>
          <div className="qweb-toggle-knob" ref={knobRef}></div>
        </div>
      </label>
      {showLabel && (
        <span className="qweb-toggle-label">
          {isChecked ? labelOn : labelOff}
        </span>
      )}
    </div>
  );
};

export default QWebToggle;
